import { Account, AccountUser, FundingAppeal, TournamentSession } from "@mixitone/models";
import { prepareFundingDonationTiersForSave, validateFundingDonationTiersForSave } from "@mixitone/util";
import { FormController } from "components/Forms/FormController";
import type { FormFieldConfig } from "components/Forms/FormFieldsDefinition";
import { FundingAppealDonationTiersField } from "components/Pages/Funding/FundingAppealDonationTiersField";
import { FUNDING_APPEAL_STATUS_SELECT_OPTIONS } from "lib/fundingStatusLabels";

interface Props {
  clubId: string;
  /** When opening from appeal detail (e.g. add application). */
  appealId?: string;
  /** When opening edit by record id (e.g. from applications list). */
  id?: string;
  attributes?: object;
  goalsOnly?: boolean;
  tiersOnly?: boolean;
  nameOnly?: boolean;
  onSubmit?: (record: FundingAppeal) => Promise<void>;
}

interface State {
  tournaments: TournamentSession[];
}

type Attributes = object;

class FundingAppealFormController extends FormController<FundingAppeal, Attributes, State, Props> {
  static override initialState = {
    ...FormController.initialState,
    tournaments: [] as TournamentSession[],
  };

  override async buildRecord(): Promise<FundingAppeal> {
    return new FundingAppeal({
      club_id: this.props.clubId,
      account_id: Account.current!.id!,
      name: "",
    });
  }

  override async findModel(id: string): Promise<FundingAppeal> {
    return FundingAppeal.query().find(id);
  }

  override async changeProps(newProps: Props) {
    await super.changeProps({
      ...newProps,
      attributes: newProps.attributes ?? {},
      id: newProps.appealId ?? newProps.id,
    });
  }

  override async saveRecord(record: FundingAppeal): Promise<void> {
    if (!record.account_id) record.account_id = Account.current!.id!;
    if (!record.club_id) record.club_id = this.props.clubId;
    if (record.tournament_session_id === "") record.tournament_session_id = undefined;
    if (this.isCreateModal && AccountUser.current?.id) {
      (record as FundingAppeal & { created_by_account_user_id?: string }).created_by_account_user_id =
        AccountUser.current.id;
    }
    const tiers = prepareFundingDonationTiersForSave(record.donation_tiers);
    validateFundingDonationTiersForSave(tiers);
    record.donation_tiers = tiers as unknown as FundingAppeal["donation_tiers"];
    // target_cents is bound directly by the form (dollars field type)
    await record.save();
  }

  override async onLoad(): Promise<void> {
    if (this.isCreateModal || this.props.goalsOnly || this.props.tiersOnly || this.props.nameOnly) return;
    const tournaments = await TournamentSession.query()
      .eq("club_id", this.props.clubId)
      .eq("is_deleted", false)
      .all();
    this.state.tournaments = tournaments;
  }

  get isCreateModal(): boolean {
    return !this.props.appealId && !this.props.id;
  }

  fields(): Record<string, FormFieldConfig> {
    if (this.props.nameOnly) {
      return {
        name: {
          type: "text",
          name: "name",
          label: "Title",
          placeholder: "e.g. Summer tournament 2025",
          required: true,
          group: "basic",
        },
        status: {
          type: "select",
          name: "status",
          label: "Status",
          options: FUNDING_APPEAL_STATUS_SELECT_OPTIONS,
          group: "basic",
        },
      } as const satisfies Record<string, FormFieldConfig>;
    }
    if (this.props.goalsOnly) {
      return {
        target_cents: {
          type: "dollars",
          name: "target_cents",
          label: "Amount to raise",
          description:
            "Total dollars you want to raise for this appeal. Progress on the appeal page is tracked against this amount.",
          placeholder: "e.g. 5000",
          min: 0,
          step: 0.01,
          group: "goals",
        },
        goal_notes: {
          type: "text",
          name: "goal_notes",
          label: "What you're raising for",
          description:
            "Short description of the project or need. Assistant-generated emails use this text as context to describe the goal in plain language.",
          placeholder: "e.g. Resurface courts and replace nets",
          inputType: "textarea",
          rows: 5,
          group: "goals",
        },
      } as const satisfies Record<string, FormFieldConfig>;
    }
    if (this.props.tiersOnly) {
      return {
        donation_tiers: {
          type: "custom",
          name: "donation_tiers",
          label: "Donation tiers",
          group: "tiers",
          component: FundingAppealDonationTiersField,
        },
      } as const satisfies Record<string, FormFieldConfig>;
    }
    if (this.isCreateModal) {
      return {
        name: {
          type: "text",
          name: "name",
          label: "Name",
          placeholder: "e.g. Summer tournament 2025",
          required: true,
          group: "basic",
        },
      } as const satisfies Record<string, FormFieldConfig>;
    }
    const tournamentOptions = [
      { value: "", label: "(None)" },
      ...this.state.tournaments.map((t) => ({
        value: t.id!,
        label: t.name ?? "",
      })),
    ];
    return {
      name: {
        type: "text",
        name: "name",
        label: "Name",
        placeholder: "e.g. Summer tournament 2025",
        required: true,
        group: "basic",
      },
      tournament_session_id: {
        type: "select",
        name: "tournament_session_id",
        label: "Tournament (optional)",
        options: tournamentOptions,
        group: "basic",
      },
      target_cents: {
        type: "dollars",
        name: "target_cents",
        label: "Amount to raise",
        description:
          "Total dollars you want to raise for this appeal. Progress on the appeal page is tracked against this amount.",
        placeholder: "e.g. 5000",
        min: 0,
        step: 0.01,
        group: "goals",
      },
      goal_notes: {
        type: "text",
        name: "goal_notes",
        label: "What you're raising for",
        description:
          "Short description of the project or need. Assistant-generated emails use this text as context to describe the goal in plain language.",
        placeholder: "e.g. Resurface courts and replace nets",
        inputType: "textarea",
        rows: 5,
        group: "goals",
      },
    } as const satisfies Record<string, FormFieldConfig>;
  }
}

export { FundingAppealFormController };
